import React, { useEffect, useState } from 'react';
import { Select, Switch } from 'antd';
import {
  defaultProjectFields,
  displayColumnName,
  fieldNames,
  guestDefaultColumnName
} from './fields';
import styles from './index.module.css';
import * as translations from './strings';
import { getLangKey } from 'models/Language';
import { updateOrganization } from '../organization';

const strings = translations[getLangKey()];

type ProjectDefaultSettings = {
  [key: string]: boolean | string;
};

type Props = {
  orgId: number;
  projectDefault: ProjectDefaultSettings;
  columns?: { name: string }[];
  shouldSave: boolean;
  setShouldSave: (shouldSave: boolean) => void;
  guestsSeeGuests: boolean;
  guestsEditTaskStatus: boolean;
  assignGuests: boolean;
};

export const ProjectDefault: React.FC<Props> = ({
  orgId,
  projectDefault: settings,
  columns = [],
  shouldSave,
  setShouldSave,
  guestsSeeGuests,
  guestsEditTaskStatus,
  assignGuests
}) => {
  const [projectDefault, setProjectDefault] = useState<ProjectDefaultSettings>({
    ...defaultProjectFields,
    ...(settings || {})
  });
  const columnNames = columns.map(column => column.name).filter(Boolean);
  const savedColumnName = String(
    projectDefault[guestDefaultColumnName] || ''
  );
  const selectedColumnName =
    columnNames.find(
      name => name.toLowerCase() === savedColumnName.toLowerCase()
    ) || columnNames[0];

  const handleToggle = (key: string) => {
    const turningOn =
      key === fieldNames.change_guest_default_column && !projectDefault[key];
    const updated = {
      ...projectDefault,
      [key]: !projectDefault[key],
      ...(turningOn && selectedColumnName
        ? { [guestDefaultColumnName]: selectedColumnName }
        : {})
    };
    setProjectDefault(updated);
  };

  useEffect(() => {
    if (shouldSave) {
      updateOrganization({
        organizationId: orgId,
        body: { projectDefault: JSON.stringify(projectDefault) },
        showSuccess: false
      });
      setShouldSave(false);
    }
  }, [shouldSave]);

  const getShow = key => {
    switch (key) {
      case fieldNames.permission:
        return guestsSeeGuests;
      case fieldNames.allow_guests_change_task_status:
        return guestsEditTaskStatus;
      case fieldNames.assign_guests:
        return assignGuests;
      default:
        return true;
    }
  };

  useEffect(() => {
    if (!selectedColumnName) return;
    if (savedColumnName.toLowerCase() === selectedColumnName.toLowerCase()) return;

    setProjectDefault(current => ({
      ...current,
      [guestDefaultColumnName]: selectedColumnName
    }));
  }, [selectedColumnName, savedColumnName]);

  const switches = Object.keys(defaultProjectFields).map(key => ({
    key,
    label: strings[key],
    checked: projectDefault[key],
    show: getShow(key),
    separator: (key === fieldNames.allow_project_summary_email ||
      key === fieldNames.permission ||
      (!guestsSeeGuests && key === fieldNames.change_guest_default_column)) && (
      <>
        <h3>
          Client{' '}
          {key === fieldNames.allow_project_summary_email
            ? 'Notifications'
            : 'Permissions'}
        </h3>
      </>
    )
  }));

  return (
    <div>
      <div className={styles.switchWrapper}>
        {switches.map(
          ({ key, label, checked, show, separator }) =>
            show && (
              <>
                {separator && separator}
                <div key={key} className={styles.switchItem}>
                  <Switch
                    checked={!!checked}
                    onChange={() => handleToggle(key)}
                    className={styles.switchLink}
                  />
                  <span>
                    {label}
                    {key === fieldNames.change_guest_default_column &&
                      selectedColumnName && (
                        <Select
                          className={styles.columnSelect}
                          disabled={!checked}
                          value={selectedColumnName}
                          options={columnNames.map(name => ({
                            value: name,
                            label: displayColumnName(name)
                          }))}
                          onChange={name =>
                            setProjectDefault(current => ({
                              ...current,
                              [guestDefaultColumnName]: name
                            }))
                          }
                          popupMatchSelectWidth={false}
                        />
                      )}
                  </span>
                </div>
              </>
            )
        )}
      </div>
    </div>
  );
};
